// src/pages/Contact.js
import React, { useState, useEffect } from 'react';
import Banner from '../../components/banner/banner';
import { useLanguage } from '../../context/LanguageContext';
import { fetchEntries } from '../../contenfulService';
import { motion } from 'framer-motion';
import './contact.css';

export default function Contact() {
  // ——— Contentful setup ———
  const [content, setContent] = useState(null);
  const { language } = useLanguage();
  const locale = language === 'en' ? 'en-US' : 'es';

  useEffect(() => {
    fetchEntries({
      content_type: 'contactUs',
      order: 'sys.createdAt',
      locale,
    })
      .then((response) => {
        setContent(response.items[0].fields);
      })
      .catch((err) => {
        console.error('Error fetching content:', err);
      });
  }, [locale]);

  // ensure we have an object to destructure
  const fields = content || {};

  // ——— pull out all of your translation/form‑label fields ———
  const {
    // contact‐page text
    contactTitle,
    contactText,
    phoneNumber,
    contactFax,
    openingHoursTitle,
    openingHoursText,
    locationTitle,
    locationInfo,

    // **form** labels & status
    contactFormTitle,
    contactFormNameLabel,
    contactFormEmailLabel,
    contactFormPhoneLabel,
    contactFormMessageLabel,
    contactFormSendButton,
    contactStatusSending,
    contactStatusSent,
    contactStatusError,
  } = fields;

  // ——— Form state & submission ———
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [phone, setPhone]     = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(contactStatusSending);

    try {
      const res = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, message }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || contactStatusError);
      setStatus(contactStatusSent);
      setName(''); setEmail(''); setPhone(''); setMessage('');
    } catch (err) {
      setStatus(contactStatusError);
    }
  };

  return (
    <div className="contactPage">
      {content && (
        <>
          <Banner imgSrc="/assets/grapes.jpeg" title={contactTitle} />

          <motion.div
            className="mt-8 ml-8 mr-8 mb-8 contact-text-container"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            {/* ——— Contact Info ——— */}
						<section className="contact-info">
            <div className="contact-text bg-offwhite rounded-lg shadow-md p-6 text-center">
              <p>
                {contactText}{' '}
                <a href={`tel:${phoneNumber}`} className="text-blue hover:underline">
                  {phoneNumber}
                </a>
              </p>
              {contactFax && <p>Fax: {contactFax}</p>}
              <h2><b>{openingHoursTitle}</b></h2>
              <p>{openingHoursText}</p>
              <h2><b>{locationTitle}</b></h2>
              <p>{locationInfo}</p>
            </div>
						</section>
            {/* ——— Contact Form ——— */}
            <div className="mt-10">

              <form onSubmit={handleSubmit} className="contactForm">
							<h2 className="text-2xl font-bold mb-4">{contactFormTitle}</h2>
                <div className="formGroup">
                  <label htmlFor="name" className="formLabel">
                    {contactFormNameLabel}
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="formInput"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="formGroup">
                  <label htmlFor="email" className="formLabel">
                    {contactFormEmailLabel}
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="formInput"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="formGroup">
                  <label htmlFor="phone" className="formLabel">
                    {contactFormPhoneLabel}
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    className="formInput"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>

                <div className="formGroup">
                  <label htmlFor="message" className="formLabel">
                    {contactFormMessageLabel}
                  </label>
                  <textarea
                    id="message"
                    className="formTextarea"
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>

                <button type="submit" className="formButton">
                  {contactFormSendButton}
                </button>
              </form>
              {status && (
                <p className="statusMessage mt-4">
                  {status}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
